import AuthAPI from '../api/auth';
import BaseActionCableConnector from '../../shared/helpers/BaseActionCableConnector';
import DashboardAudioNotificationHelper from './AudioAlerts/DashboardAudioNotificationHelper';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { emitter } from 'shared/helpers/mitt';
import { useImpersonation } from 'dashboard/composables/useImpersonation';

const { isImpersonating } = useImpersonation();

// whatsapp_quota.updated can fire once per message in a busy conversation (every charge
// and reversal path dispatches it — see Leadster::WhatsappQuota::Tracker), so refetching
// the account on every single one would hammer the API; throttle it.
const WHATSAPP_QUOTA_REFRESH_THROTTLE_MS = 15000;

class ActionCableConnector extends BaseActionCableConnector {
  constructor(app, pubsubToken) {
    const { websocketURL = '' } = window.chatwootConfig || {};
    super(app, pubsubToken, websocketURL);
    this.CancelTyping = [];
    this.events = {
      'message.created': this.onMessageCreated,
      'message.updated': this.onMessageUpdated,
      'conversation.created': this.onConversationCreated,
      'conversation.status_changed': this.onStatusChange,
      'user:logout': this.onLogout,
      'page:reload': this.onReload,
      'assignee.changed': this.onAssigneeChanged,
      'conversation.typing_on': this.onTypingOn,
      'conversation.typing_off': this.onTypingOff,
      'conversation.contact_changed': this.onConversationContactChange,
      'presence.update': this.onPresenceUpdate,
      'contact.deleted': this.onContactDelete,
      'contact.updated': this.onContactUpdate,
      'conversation.mentioned': this.onConversationMentioned,
      'notification.created': this.onNotificationCreated,
      'notification.deleted': this.onNotificationDeleted,
      'notification.updated': this.onNotificationUpdated,
      'conversation.read': this.onConversationRead,
      'conversation.updated': this.onConversationUpdated,
      'account.cache_invalidated': this.onCacheInvalidate,
      'copilot.message.created': this.onCopilotMessageCreated,
      'whatsapp_flow_enrollment.updated': this.onWhatsappFlowEnrollmentUpdated,
      'whatsapp_quota.updated': this.refreshWhatsappQuotaState,
      'conversation_follow_up.updated': this.onConversationFollowUpUpdated,
    };
  }

  // eslint-disable-next-line class-methods-use-this
  onReconnect = () => {
    emitter.emit(BUS_EVENTS.WEBSOCKET_RECONNECT);
  };

  // eslint-disable-next-line class-methods-use-this
  onDisconnected = () => {
    emitter.emit(BUS_EVENTS.WEBSOCKET_DISCONNECT);
  };

  isAValidEvent = data => {
    return this.app.$store.getters.getCurrentAccountId === data.account_id;
  };

  onMessageUpdated = data => {
    this.app.$store.dispatch('updateMessage', data);
  };

  onPresenceUpdate = data => {
    if (isImpersonating.value) return;
    this.app.$store.dispatch('contacts/updatePresence', data.contacts);
    this.app.$store.dispatch('agents/updatePresence', data.users);
    this.app.$store.dispatch('setCurrentUserAvailability', data.users);
  };

  onConversationContactChange = payload => {
    const { meta = {}, id: conversationId } = payload;
    const { sender } = meta || {};
    if (conversationId) {
      this.app.$store.dispatch('updateConversationContact', {
        conversationId,
        ...sender,
      });
    }
  };

  onAssigneeChanged = payload => {
    const { id } = payload;
    if (id) {
      this.app.$store.dispatch('updateConversation', payload);
    }
    this.fetchConversationStats();
  };

  onConversationCreated = data => {
    this.app.$store.dispatch('addConversation', data);
    this.fetchConversationStats();
  };

  onConversationRead = data => {
    this.app.$store.dispatch('updateConversation', data);
  };

  // eslint-disable-next-line class-methods-use-this
  onLogout = () => AuthAPI.logout();

  onMessageCreated = data => {
    const {
      conversation: { last_activity_at: lastActivityAt },
      conversation_id: conversationId,
    } = data;
    DashboardAudioNotificationHelper.onNewMessage(data);
    this.app.$store.dispatch('addMessage', data);
    this.app.$store.dispatch('updateConversationLastActivity', {
      lastActivityAt,
      conversationId,
    });
  };

  // Charging or reversing WhatsApp quota updates account_usage_cycles/account_usage_dailies
  // directly, without touching the account or its cache keys, so onCacheInvalidate never
  // fires for it — Leadster::WhatsappQuota::Tracker dispatches whatsapp_quota.updated
  // instead, from every charge/reversal path (Message create/update, direct flow sends,
  // campaigns), so an already-open admin session's usage banner and composer state don't go
  // stale until an unrelated account update or reload.
  refreshWhatsappQuotaState = () => {
    const now = Date.now();
    const elapsed = this.lastQuotaRefreshAt
      ? now - this.lastQuotaRefreshAt
      : null;
    if (elapsed === null || elapsed >= WHATSAPP_QUOTA_REFRESH_THROTTLE_MS) {
      this.lastQuotaRefreshAt = now;
      this.app.$store.dispatch('accounts/get', { silent: true });
      return;
    }

    // A leading-edge-only throttle would silently drop this event. If it's the one that
    // pushes usage to 100%, the next send is blocked server-side and charges nothing, so it
    // never dispatches whatsapp_quota.updated itself — with no trailing refresh scheduled,
    // the banner/composer would be stuck at the pre-burst ratio indefinitely. Schedule one
    // trailing refresh instead (coalesced: a burst only ever has one pending).
    if (!this.quotaRefreshTrailingTimeout) {
      this.quotaRefreshTrailingTimeout = setTimeout(() => {
        this.quotaRefreshTrailingTimeout = null;
        this.lastQuotaRefreshAt = Date.now();
        this.app.$store.dispatch('accounts/get', { silent: true });
      }, WHATSAPP_QUOTA_REFRESH_THROTTLE_MS - elapsed);
    }
  };

  // eslint-disable-next-line class-methods-use-this
  onReload = () => window.location.reload();

  onStatusChange = data => {
    this.app.$store.dispatch('updateConversation', data);
    this.fetchConversationStats();
  };

  onConversationUpdated = data => {
    this.app.$store.dispatch('updateConversation', data);
    this.fetchConversationStats();
  };

  onTypingOn = ({ conversation, user }) => {
    const conversationId = conversation.id;

    this.clearTimer(conversationId);
    this.app.$store.dispatch('conversationTypingStatus/create', {
      conversationId,
      user,
    });
    this.initTimer({ conversation, user });
  };

  onTypingOff = ({ conversation, user }) => {
    const conversationId = conversation.id;

    this.clearTimer(conversationId);
    this.app.$store.dispatch('conversationTypingStatus/destroy', {
      conversationId,
      user,
    });
  };

  onConversationMentioned = data => {
    this.app.$store.dispatch('addMentions', data);
  };

  clearTimer = conversationId => {
    const timerEvent = this.CancelTyping[conversationId];

    if (timerEvent) {
      clearTimeout(timerEvent);
      this.CancelTyping[conversationId] = null;
    }
  };

  initTimer = ({ conversation, user }) => {
    const conversationId = conversation.id;
    // Turn off typing automatically after 30 seconds
    this.CancelTyping[conversationId] = setTimeout(() => {
      this.onTypingOff({ conversation, user });
    }, 30000);
  };

  // eslint-disable-next-line class-methods-use-this
  fetchConversationStats = () => {
    emitter.emit('fetch_conversation_stats');
  };

  onContactDelete = data => {
    this.app.$store.dispatch(
      'contacts/deleteContactThroughConversations',
      data.id
    );
    this.fetchConversationStats();
  };

  onContactUpdate = data => {
    this.app.$store.dispatch('contacts/updateContact', data);
  };

  onNotificationCreated = data => {
    this.app.$store.dispatch('notifications/addNotification', data);
    if (
      data?.notification?.notification_type === 'agent_execution_report_ready'
    ) {
      emitter.emit(BUS_EVENTS.SHOW_TOAST, {
        message:
          data.notification.push_message_title ||
          data.notification.push_message_body,
      });
    }
  };

  onNotificationDeleted = data => {
    this.app.$store.dispatch('notifications/deleteNotification', data);
  };

  onNotificationUpdated = data => {
    this.app.$store.dispatch('notifications/updateNotification', data);
  };

  onCopilotMessageCreated = data => {
    this.app.$store.dispatch('copilotMessages/upsert', data);
  };

  onWhatsappFlowEnrollmentUpdated = data => {
    if (!data?.enrollment) return;
    this.app.$store.dispatch(
      'whatsappFlows/receiveEnrollmentUpdate',
      data.enrollment
    );
  };

  onConversationFollowUpUpdated = data => {
    if (!data?.follow_up) return;
    this.app.$store.dispatch(
      'conversationFollowUps/receiveLiveUpdate',
      data.follow_up
    );
  };

  onCacheInvalidate = data => {
    const keys = data.cache_keys;
    this.app.$store.dispatch('labels/revalidate', { newKey: keys.label });
    this.app.$store.dispatch('inboxes/revalidate', { newKey: keys.inbox });
    this.app.$store.dispatch('teams/revalidate', { newKey: keys.team });
    this.app.$store.dispatch('agents/revalidate', {
      newKey: keys.account_user,
    });
    this.app.$store.dispatch('cannedResponse/revalidate', {
      newKey: keys.canned_response,
    });
    this.app.$store.dispatch('accounts/limits');
    // Refreshes account.whatsapp_quota (banner + composer block) so a plan/franchise
    // change (extra packages, multiplier, cycle) applies live, without a page reload.
    // silent: true — this fires as a background refresh while the agent may be
    // mid-conversation; toggling isFetchingItem would otherwise unmount the whole
    // router-view (composer included) and drop whatever they were typing.
    this.app.$store.dispatch('accounts/get', { silent: true });
    // Lets screens outside the Vuex agents/teams/inboxes modules (e.g. the
    // onboarding wizard) know an attendant, team, inbox, canned response, or
    // label changed elsewhere.
    emitter.emit(BUS_EVENTS.AGENTS_CHANGED);
    emitter.emit(BUS_EVENTS.TEAMS_CHANGED);
    emitter.emit(BUS_EVENTS.INBOX_CHANGED);
    emitter.emit(BUS_EVENTS.CANNED_RESPONSES_CHANGED);
    emitter.emit(BUS_EVENTS.LABEL_CHANGED);
  };
}

export default {
  init(store, pubsubToken) {
    return new ActionCableConnector({ $store: store }, pubsubToken);
  },
};
